import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row
} from 'react-bootstrap';
import { Info, Layers, Play, Search, Sparkles, Wand2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  toMcpApiErrorMessage,
  useLazyListMcpChatSkillsCatalogQuery,
  useListMcpChatSkillCategoriesQuery
} from '../api/mcpApi';
import type { McpChatSkillCatalogItem } from '../types/mcpApiTypes';
import { buildSkillUserMessage } from '../utils/skillMarkers';
import { McpSkillsChipsPreload } from '../components/McpSkillsChipsPreload';
import { McpSkillsListPreload } from '../components/McpSkillsListPreload';

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 280;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export default function SkillsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [category, setCategory] = useState<string | null>(null);
  const [items, setItems] = useState<McpChatSkillCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);

  const { data: categoriesData, isLoading: categoriesLoading } =
    useListMcpChatSkillCategoriesQuery();
  const categories = categoriesData?.categories ?? [];
  const [fetchCatalog] = useLazyListMcpChatSkillsCatalogQuery();

  const loadPage = useCallback(
    async (offset: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setError(null);
      }

      try {
        const result = await fetchCatalog({
          q: debouncedSearch || undefined,
          category: category || undefined,
          limit: PAGE_SIZE,
          offset
        }).unwrap();
        if (requestId !== requestIdRef.current) return;

        setItems(prev =>
          append ? [...prev, ...(result.items ?? [])] : (result.items ?? [])
        );
        setTotal(result.total ?? 0);
        setHasMore(Boolean(result.hasMore));
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(toMcpApiErrorMessage(err, 'خطا در دریافت مهارت‌ها'));
        if (!append) {
          setItems([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [category, debouncedSearch, fetchCatalog]
  );

  useEffect(() => {
    void loadPage(0, false);
  }, [loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          void loadPage(items.length, true);
        }
      },
      { rootMargin: '240px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, items.length, loadPage, loading, loadingMore]);

  const runSkill = useCallback(
    (skill: McpChatSkillCatalogItem) => {
      navigate('/chat', {
        state: {
          mcpNewChat: Date.now(),
          initialPrompt: buildSkillUserMessage({
            command: skill.command,
            title: skill.title
          })
        }
      });
    },
    [navigate]
  );

  const clearFilters = () => {
    setSearch('');
    setCategory(null);
  };

  const hasFilters = Boolean(search.trim() || category);
  const totalLabel = useMemo(() => {
    if (loading && items.length === 0) return 'در حال بارگذاری…';
    return `${total.toLocaleString('fa-IR')} مهارت`;
  }, [items.length, loading, total]);

  return (
    <div className="mcp-skills-page">
      <style>{`
        .mcp-skills-page {
          --skill-ink: var(--bs-body-color, var(--falcon-body-color, #344050));
          --skill-muted: var(--bs-secondary-color, var(--falcon-secondary-color, #748194));
          --skill-card: var(--bs-tertiary-bg, var(--falcon-emphasis-bg, var(--bs-body-bg, #fff)));
          --skill-input: var(--bs-body-bg, var(--falcon-body-bg, #fff));
          --skill-search-bg: #fff;
          --skill-line: var(--bs-border-color, var(--falcon-border-color, rgba(0,0,0,.08)));
          --skill-primary: var(--bs-primary, var(--falcon-primary, #2c7be5));
          --skill-soft: color-mix(in srgb, var(--skill-primary) 12%, transparent);
          --skill-hero-a: color-mix(in srgb, var(--skill-primary) 16%, transparent);
          --skill-hero-b: color-mix(in srgb, var(--skill-primary) 10%, transparent);
          padding-bottom: 2.5rem;
        }
        [data-bs-theme='dark'] .mcp-skills-page {
          --skill-card: var(--falcon-emphasis-bg, var(--bs-tertiary-bg, #121e2d));
          --skill-input: var(--falcon-body-bg, var(--bs-body-bg, #0b1727));
          --skill-search-bg: var(--falcon-card-bg, var(--bs-card-bg, #0e1c2f));
          --skill-muted: var(--bs-secondary-color, #9da9bb);
          --skill-hero-a: color-mix(in srgb, var(--skill-primary) 14%, transparent);
          --skill-hero-b: color-mix(in srgb, var(--skill-primary) 8%, transparent);
        }
        .mcp-skills-hero {
          position: relative;
          overflow: hidden;
          border-radius: 1.35rem !important;
          border-color: var(--skill-line) !important;
          background:
            radial-gradient(1200px 280px at 100% -20%, var(--skill-hero-a), transparent 55%),
            radial-gradient(900px 240px at 0% 0%, var(--skill-hero-b), transparent 50%),
            var(--bs-card-bg, var(--skill-card));
          margin-bottom: 1.25rem;
        }
        .mcp-skills-hero .card-body {
          padding: 1.5rem 1.5rem 1.25rem;
        }
        .mcp-skills-hero-title {
          font-size: clamp(1.35rem, 2vw, 1.75rem);
          font-weight: 700;
          letter-spacing: -0.02em;
          margin: 0;
          color: var(--skill-ink);
        }
        .mcp-skills-hero-copy {
          margin: 0.45rem 0 0;
          color: var(--skill-muted);
          font-size: 0.92rem;
          line-height: 1.75;
        }
        .mcp-skills-filters {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.45rem;
          margin-bottom: 1rem;
        }
        .mcp-skills-filters .mcp-skills-chips {
          display: contents;
        }
        .mcp-skills-search {
          position: relative;
          flex: 0 1 270px;
          min-width: min(100%, 220px);
          margin-inline-end: 0.3rem;
        }
        @media (max-width: 575.98px) {
          .mcp-skills-search { flex-basis: 100%; margin-inline-end: 0; }
        }
        .mcp-skills-search .form-control {
          padding-inline-start: 2.35rem;
          padding-inline-end: 2.2rem;
          border-radius: 999px;
          min-height: 2.15rem;
          font-size: 0.84rem;
          border-color: var(--skill-line);
          background: var(--skill-search-bg);
          color: var(--skill-ink);
        }
        .mcp-skills-search .form-control::placeholder {
          color: var(--skill-muted);
          opacity: 0.85;
        }
        .mcp-skills-search .form-control:focus {
          border-color: color-mix(in srgb, var(--skill-primary) 55%, var(--skill-line));
          box-shadow: 0 0 0 0.2rem var(--skill-soft);
          background: var(--skill-search-bg);
          color: var(--skill-ink);
        }
        .mcp-skills-search-icon,
        .mcp-skills-search-clear {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          color: var(--skill-muted);
          border: 0;
          background: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .mcp-skills-search-icon { inset-inline-start: 0.85rem; pointer-events: none; }
        .mcp-skills-search-clear { inset-inline-end: 0.55rem; width: 1.75rem; height: 1.75rem; border-radius: 999px; }
        .mcp-skills-search-clear:hover { background: var(--skill-soft); color: var(--skill-ink); }
        .mcp-skills-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.45rem;
          margin-bottom: 1rem;
        }
        .mcp-skills-chip {
          border: 1px solid var(--skill-line);
          background: var(--skill-card);
          color: var(--skill-ink);
          border-radius: 999px;
          padding: 0.3rem 0.45rem 0.3rem 0.75rem;
          font-size: 0.8rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          transition: background .15s ease, border-color .15s ease, color .15s ease, transform .15s ease;
        }
        .mcp-skills-chip:hover {
          transform: translateY(-1px);
          border-color: color-mix(in srgb, var(--skill-primary) 45%, var(--skill-line));
        }
        .mcp-skills-chip.is-active {
          background: var(--skill-soft);
          border-color: color-mix(in srgb, var(--skill-primary) 45%, var(--skill-line));
          color: var(--skill-primary);
        }
        .mcp-skills-chip-count {
          font-size: 0.7rem;
          font-weight: 700;
          min-width: 1.4rem;
          justify-content: center;
        }
        .mcp-skills-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 0.85rem;
          color: var(--skill-muted);
          font-size: 0.82rem;
        }
        .mcp-skill-card {
          height: 100%;
          border-radius: 1.15rem !important;
          border-color: var(--skill-line) !important;
          transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
          overflow: hidden;
        }
        .mcp-skill-card:hover {
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--skill-primary) 40%, var(--skill-line)) !important;
          box-shadow: 0 14px 34px color-mix(in srgb, var(--skill-primary) 14%, transparent);
        }
        [data-bs-theme='dark'] .mcp-skill-card:hover {
          box-shadow: 0 16px 36px rgba(0, 0, 0, 0.35);
        }
        .mcp-skill-card .card-body {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding: 1.1rem 1.15rem 0.85rem;
        }
        .mcp-skill-card .card-footer {
          background: transparent;
          border-top: 1px solid var(--skill-line);
          padding: 0.85rem 1.15rem 1rem;
        }
        .mcp-skill-card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.75rem;
        }
        .mcp-skill-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          border-radius: 999px;
          padding: 0.22rem 0.65rem;
          font-size: 0.72rem;
          font-weight: 700;
          background: color-mix(in srgb, var(--skill-primary) 14%, transparent);
          color: var(--skill-primary);
        }
        [data-bs-theme='dark'] .mcp-skill-badge {
          background: color-mix(in srgb, var(--skill-primary) 22%, transparent);
          color: color-mix(in srgb, var(--skill-primary) 70%, white);
        }
        .mcp-skill-command {
          font-size: 0.72rem;
          color: var(--skill-muted);
          direction: ltr;
          unicode-bidi: isolate;
        }
        .mcp-skill-title {
          margin: 0;
          font-size: 1.02rem;
          font-weight: 700;
          letter-spacing: -0.015em;
          color: var(--skill-ink);
          line-height: 1.45;
        }
        .mcp-skill-short {
          margin: 0;
          color: var(--skill-muted);
          font-size: 0.86rem;
          line-height: 1.7;
        }
        .mcp-skill-popover {
          max-width: min(22rem, calc(100vw - 2rem));
          border-radius: 0.9rem;
        }
        [data-bs-theme='dark'] .mcp-skill-popover {
          --falcon-popover-bg: #1c2b40;
          --falcon-popover-header-bg: #22344d;
          --falcon-popover-header-color: #f1f5fa;
          --falcon-popover-body-color: #d8e2ef;
          --falcon-popover-border-color: rgba(255, 255, 255, 0.12);
          --falcon-popover-arrow-border: var(--falcon-popover-border-color);
          background-color: var(--falcon-popover-bg);
          border-color: var(--falcon-popover-border-color);
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.55);
        }
        [data-bs-theme='dark'] .mcp-skill-popover .popover-header {
          background-color: var(--falcon-popover-header-bg);
          color: var(--falcon-popover-header-color);
          border-bottom-color: var(--falcon-popover-border-color);
        }
        [data-bs-theme='dark'] .mcp-skill-popover .popover-body {
          color: var(--falcon-popover-body-color);
        }
        .mcp-skill-popover .popover-header {
          font-size: 0.88rem;
          font-weight: 700;
          border-top-left-radius: 0.9rem;
          border-top-right-radius: 0.9rem;
        }
        .mcp-skill-popover .popover-body {
          font-size: 0.84rem;
          line-height: 1.85;
          white-space: pre-line;
        }
        .mcp-skill-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .mcp-skills-empty {
          border: 1px dashed var(--skill-line);
          border-radius: 1.15rem;
          padding: 2.5rem 1.25rem;
          text-align: center;
          color: var(--skill-muted);
          background: var(--skill-card);
        }
        [data-bs-theme='dark'] .mcp-skills-page .btn-falcon-default {
          background: color-mix(in srgb, var(--skill-input) 80%, white 6%);
          border-color: var(--skill-line);
          color: var(--skill-ink);
        }
      `}</style>

      <Card className="mcp-skills-hero">
        <Card.Body>
          <div className="d-flex align-items-start gap-3">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 48,
                height: 48,
                background:
                  'color-mix(in srgb, var(--bs-primary, var(--falcon-primary)) 16%, transparent)',
                color: 'var(--bs-primary, var(--falcon-primary))'
              }}
            >
              <Wand2 size={22} strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <Card.Title as="h1" className="mcp-skills-hero-title">
                مهارت‌های دستیار
              </Card.Title>
              <Card.Text className="mcp-skills-hero-copy">
                ابزارهای تخصصی سئو، آماده برای استفاده در گفتگو: از تحقیق کلمات
                کلیدی و کیفیت محتوا تا چکاپ فنی صفحه و سایت، اسکیما، سئوی محلی و
                آمادگی برای جستجوی هوش مصنوعی. مهارت را انتخاب کنید، آدرس صفحه
                یا اطلاعات لازم را وارد کنید و گزارشی با امتیاز و پیشنهادهای
                اولویت‌بندی‌شده بگیرید.
              </Card.Text>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="mcp-skills-filters">
        <div className="mcp-skills-search">
          <Search size={16} className="mcp-skills-search-icon" aria-hidden />
          <Form.Control
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="جستجو بر اساس نام مهارت یا فرمان…"
            aria-label="جستجوی مهارت"
          />
          {search ? (
            <button
              type="button"
              className="mcp-skills-search-clear"
              onClick={() => setSearch('')}
              aria-label="پاک کردن جستجو"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>

        {categoriesLoading && categories.length === 0 ? (
          <McpSkillsChipsPreload chips={5} />
        ) : (
          <div
            className="mcp-skills-chips"
            role="tablist"
            aria-label="دسته‌بندی مهارت‌ها"
          >
            <button
              type="button"
              className={cn('mcp-skills-chip', !category && 'is-active')}
              onClick={() => setCategory(null)}
            >
              همه
            </button>
            {categories.map(row => (
              <button
                key={row.category}
                type="button"
                className={cn(
                  'mcp-skills-chip',
                  category === row.category && 'is-active'
                )}
                onClick={() =>
                  setCategory(prev =>
                    prev === row.category ? null : row.category
                  )
                }
              >
                <span>{row.category}</span>
                <Badge
                  bg={category === row.category ? 'primary' : 'secondary'}
                  pill
                  className="mcp-skills-chip-count"
                  style={
                    category === row.category
                      ? {
                          backgroundColor: 'var(--falcon-primary)',
                          color: 'var(--falcon-primary-text-emphasis, #fff)'
                        }
                      : undefined
                  }
                >
                  {row.count.toLocaleString('fa-IR')}
                </Badge>
              </button>
            ))}
          </div>
        )}

        {hasFilters ? (
          <Button
            size="sm"
            variant="falcon-default"
            className="rounded-pill"
            onClick={clearFilters}
          >
            پاک کردن فیلترها
          </Button>
        ) : null}
      </div>

      <div className="mcp-skills-meta">
        <span className="d-inline-flex align-items-center gap-1">
          <Layers size={14} aria-hidden />
          {totalLabel}
        </span>
        {category ? (
          <span className="d-inline-flex align-items-center gap-1">
            <Sparkles size={14} aria-hidden />
            فیلتر: {category}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="mcp-skills-empty mb-3">
          <p className="mb-2 text-danger">{error}</p>
          <Button
            size="sm"
            variant="falcon-primary"
            onClick={() => void loadPage(0, false)}
          >
            تلاش دوباره
          </Button>
        </div>
      ) : null}

      {!error && loading && items.length === 0 ? (
        <McpSkillsListPreload cards={6} />
      ) : null}

      {!error && !loading && items.length === 0 ? (
        <div className="mcp-skills-empty">
          <Wand2 size={28} className="mb-2 opacity-50" />
          <div className="fw-semibold mb-1">مهارتی پیدا نشد</div>
          <div className="fs--1">
            عبارت جستجو یا دسته را تغییر دهید و دوباره امتحان کنید.
          </div>
        </div>
      ) : null}

      <Row className="g-3">
        {items.map(skill => (
          <Col key={skill.id} xs={12} md={6} xl={4}>
            <Card className="mcp-skill-card h-100">
              <Card.Body>
                <div className="mcp-skill-card-top">
                  <span className="mcp-skill-badge">
                    <Sparkles size={12} aria-hidden />
                    {skill.category}
                  </span>
                  <code className="mcp-skill-command">/{skill.command}</code>
                </div>

                <Card.Title as="h2" className="mcp-skill-title">
                  {skill.title}
                </Card.Title>
                <Card.Text className="mcp-skill-short">
                  {skill.shortDescription}
                </Card.Text>
              </Card.Body>

              <Card.Footer>
                <div className="mcp-skill-actions">
                  <Button
                    size="sm"
                    variant="falcon-primary"
                    className="rounded-pill d-inline-flex align-items-center gap-1"
                    onClick={() => runSkill(skill)}
                  >
                    <Play size={14} aria-hidden />
                    اجرای مهارت
                  </Button>
                  {skill.description ? (
                    <OverlayTrigger
                      trigger="click"
                      placement="top"
                      rootClose
                      overlay={
                        <Popover
                          id={`mcp-skill-popover-${skill.id}`}
                          className="mcp-skill-popover"
                        >
                          <Popover.Header as="h3">{skill.title}</Popover.Header>
                          <Popover.Body>{skill.description}</Popover.Body>
                        </Popover>
                      }
                    >
                      <Button
                        size="sm"
                        variant="falcon-default"
                        className="rounded-pill d-inline-flex align-items-center gap-1"
                      >
                        <Info size={14} aria-hidden />
                        چه گزارشی می‌دهد؟
                      </Button>
                    </OverlayTrigger>
                  ) : null}
                </div>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      <div ref={sentinelRef} className="py-3" />

      {loadingMore ? <McpSkillsListPreload cards={3} /> : null}

      {!hasMore && items.length > 0 ? (
        <div className="text-center text-body-tertiary fs--1 pt-1">
          همهٔ مهارت‌های این فیلتر نمایش داده شد
        </div>
      ) : null}
    </div>
  );
}
